import React, { useState, useEffect, useCallback } from "react";
import { GovernanceHeader } from "@auction-eth/ui-kit";
import { CaseQueuePage } from "./pages/CaseQueuePage.js";
import { CaseDetailPage } from "./pages/CaseDetailPage.js";
import { InitiateCasePage } from "./pages/InitiateCasePage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { HubOpsPipelinePage } from "./pages/HubOpsPipelinePage.js";
import { MetricsDashboardPage } from "./pages/MetricsDashboardPage.js";
import { ProfilePage } from "./pages/ProfilePage.js";
import { ReportsPage } from "./pages/ReportsPage.js";
import { ComplaintsPage } from "./pages/ComplaintsPage.js";
import { HelpDrawer } from "./components/HelpDrawer.js";
import { NotificationDrawer } from "./components/NotificationDrawer.js";
import { isAuthenticated, clearTokens } from "./services/auth.js";
import { api } from "./services/api.js";

type View =
  | { name: "queue" }
  | { name: "detail"; caseId: string }
  | { name: "initiate" }
  | { name: "hub_ops" }
  | { name: "metrics" }
  | { name: "reports" }
  | { name: "complaints" }
  | { name: "profile" };

export function App(): React.ReactElement {
  const [authed, setAuthed] = useState<boolean>(() => isAuthenticated());
  const [view, setView] = useState<View>({ name: "metrics" });
  const [userName, setUserName] = useState("Institution Operator");
  const [userRole, setUserRole] = useState("institution_operator");
  const [helpOpen, setHelpOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (authed) {
      void api.auth.me().then((res) => {
        setUserName(res.data.name);
        setUserRole(res.data.role);
      }).catch(() => {
        clearTokens();
        setAuthed(false);
      });
    }
  }, [authed]);

  function handleLogout() {
    clearTokens();
    setAuthed(false);
    setView({ name: "metrics" });
  }

  const handleUnreadCountChange = useCallback((count: number) => {
    setUnreadCount(count);
  }, []);

  if (!authed) {
    return <LoginPage onLogin={() => setAuthed(true)} />;
  }

  const NAV_PILLS = [
    { id: "metrics", label: "Metrics" },
    { id: "queue", label: "Case Queue" },
    { id: "reports", label: "Reports" },
    { id: "complaints", label: "Support" },
    { id: "hub_ops", label: "Hub Ops" },
    { id: "profile", label: `👤 ${userName.split(" ")[0] ?? "Me"}` },
  ];

  function activeNav(): string {
    if (view.name === "metrics") return "metrics";
    if (view.name === "reports") return "reports";
    if (view.name === "complaints") return "complaints";
    if (view.name === "hub_ops") return "hub_ops";
    if (view.name === "profile") return "profile";
    return "queue";
  }

  return (
    <div>
      <GovernanceHeader
        appName="Institution Portal"
        userName={userName}
        userRole={userRole}
        onLogout={handleLogout}
        navItems={NAV_PILLS}
        activeNavItem={activeNav()}
        onNavSelect={(id) => {
          if (id === "hub_ops") setView({ name: "hub_ops" });
          else if (id === "metrics") setView({ name: "metrics" });
          else if (id === "reports") setView({ name: "reports" });
          else if (id === "complaints") setView({ name: "complaints" });
          else if (id === "profile") setView({ name: "profile" });
          else setView({ name: "queue" });
        }}
        rightActions={
          <button
            id="notification-bell-btn"
            onClick={() => setNotifOpen(true)}
            aria-label="Open notifications"
            title="Notifications"
            style={{
              position: "relative",
              width: 36, height: 36, borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.3)",
              cursor: "pointer", fontSize: 16,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", flexShrink: 0,
            }}
          >
            🔔
            {unreadCount > 0 && (
              <span style={{
                position: "absolute", top: -3, right: -3,
                background: "#dc2626", color: "#fff",
                borderRadius: "9999px", minWidth: 18, height: 18,
                fontSize: 10, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "0 4px",
              }}>
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        }
      />

      {/* Help button — fixed bottom-right */}
      <button
        id="help-trigger-btn"
        onClick={() => setHelpOpen(true)}
        aria-label="Open user guide"
        title="Help & User Guide"
        style={{
          position: "fixed", bottom: 24, right: 24,
          width: 48, height: 48, borderRadius: "50%",
          background: "var(--brand)", color: "#fff",
          border: "none", cursor: "pointer", fontSize: 20,
          boxShadow: "0 4px 16px rgba(99,102,241,0.4)",
          zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1.1)"; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; }}
      >
        ?
      </button>

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
      <NotificationDrawer
        open={notifOpen}
        onClose={() => setNotifOpen(false)}
        unreadCount={unreadCount}
        onUnreadCountChange={handleUnreadCountChange}
        onNavigateToCase={(caseId) => setView({ name: "detail", caseId })}
      />

      <main className="page-content">
        {view.name === "queue" && (
          <CaseQueuePage
            onSelectCase={(caseId) => setView({ name: "detail", caseId })}
            onInitiateCase={() => setView({ name: "initiate" })}
          />
        )}
        {view.name === "detail" && (
          <CaseDetailPage
            caseId={view.caseId}
            onBack={() => setView({ name: "queue" })}
          />
        )}
        {view.name === "initiate" && (
          <InitiateCasePage
            onSuccess={(caseId) => setView({ name: "detail", caseId })}
            onCancel={() => setView({ name: "queue" })}
          />
        )}
        {view.name === "hub_ops" && (
          <HubOpsPipelinePage />
        )}
        {view.name === "metrics" && (
          <MetricsDashboardPage />
        )}
        {view.name === "reports" && (
          <ReportsPage />
        )}
        {view.name === "complaints" && (
          <ComplaintsPage onNavigateToCase={(caseId) => setView({ name: "detail", caseId })} />
        )}
        {view.name === "profile" && (
          <ProfilePage />
        )}
      </main>
    </div>
  );
}
